import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Trophy, Edit3 } from 'lucide-react';
import { PairwiseSheet } from './PairwiseSheet';

interface RankedBook {
  book_id: string;
  tier: number;
  score: number;
  position?: number;
  placement_complete: boolean;
  books: {
    title: string;
    authors: string[];
    cover_url?: string;
  };
  placementState?: any;
  comparison?: any;
}

interface GlobalLeaderboardProps {
  userId?: string;
  isOwnProfile?: boolean;
}

export function GlobalLeaderboard({ userId, isOwnProfile = false }: GlobalLeaderboardProps) {
  const [rankedBooks, setRankedBooks] = useState<RankedBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingBook, setEditingBook] = useState<RankedBook | null>(null);
  const [pairwiseCandidates, setPairwiseCandidates] = useState<any[]>([]);

  useEffect(() => {
    if (userId) {
      loadGlobalLeaderboard();
    }
  }, [userId]);

  // Listen for ranking updates from other components
  useEffect(() => {
    const handleRankingsUpdate = () => {
      loadGlobalLeaderboard();
    };

    window.addEventListener('rankings-updated', handleRankingsUpdate);
    return () => {
      window.removeEventListener('rankings-updated', handleRankingsUpdate);
    };
  }, [userId]);

  const loadGlobalLeaderboard = async () => {
    if (!userId) return;

    try {
      // Get all user books with ratings and their tier scores
      const { data: userBooksData, error: userBooksError } = await supabase
        .from('user_books')
        .select(`
          book_id,
          rating_star,
          books!user_books_book_id_fkey (
            title,
            authors,
            cover_url
          )
        `)
        .eq('user_id', userId)
        .not('rating_star', 'is', null);

      if (userBooksError) {
        throw userBooksError;
      }

      if (!userBooksData || userBooksData.length === 0) {
        setRankedBooks([]);
        return;
      }

      // Get tier scores with positions for these books
      const bookIds = userBooksData.map(book => book.book_id);
      const { data: tierScoresData, error: tierScoresError } = await supabase
        .from('user_tier_scores')
        .select('book_id, tier, score, position, placement_complete')
        .eq('user_id', userId)
        .in('book_id', bookIds);

      if (tierScoresError) {
        console.error('Error loading tier scores:', tierScoresError);
      }

      // Get book corrections for these books
      const { data: correctionsData } = await supabase
        .from('book_corrections')
        .select('book_id, corrected_title, corrected_authors, corrected_cover_url')
        .in('book_id', bookIds);

      // Combine data and create rankings with corrections applied
      // Only include books where placement is complete
      const rankings: RankedBook[] = userBooksData
        .map(book => {
          const tierScore = tierScoresData?.find(
            ts => ts.book_id === book.book_id && ts.tier === book.rating_star
          );
          const correction = correctionsData?.find(c => c.book_id === book.book_id);
          
          return {
            book_id: book.book_id,
            tier: book.rating_star,
            score: tierScore?.score ?? 1500,
            position: tierScore?.position,
            placement_complete: tierScore?.placement_complete ?? false,
            books: {
              title: correction?.corrected_title || book.books.title,
              authors: correction?.corrected_authors || book.books.authors,
              cover_url: correction?.corrected_cover_url || book.books.cover_url
            }
          };
        })
        ;

      // Sort by tier (desc) then by position (asc)
      rankings.sort((a, b) => {
        if (a.tier !== b.tier) {
          return b.tier - a.tier;
        }
        // Use position if both have it
        if (a.position !== undefined && b.position !== undefined) {
          return a.position - b.position;
        }
        // Fall back to score
        if (a.position === undefined && b.position === undefined) {
          return b.score - a.score;
        }
        // Prioritize books with positions
        return a.position !== undefined ? -1 : 1;
      });

      setRankedBooks(rankings);
    } catch (error) {
      console.error('Error loading global leaderboard:', error);
      toast.error('Failed to load rankings');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRanking = async (book: RankedBook) => {
    if (!isOwnProfile) return;

    try {
      // Trigger placement algorithm to re-rank this book
      const { data, error } = await supabase.functions.invoke('placement-algorithm', {
        body: {
          userId,
          bookId: book.book_id,
          tier: book.tier
        }
      });

      if (error) throw error;

      if (data.needsComparison && data.comparison) {
        // Set up pairwise comparison with the placement algorithm's state
        setEditingBook({
          ...book,
          placementState: data.placementState,
          comparison: data.comparison
        });
      } else if (data.finalPosition) {
        toast.success(`Placed at #${data.finalPosition} in your ${book.tier}★ tier`);
        loadGlobalLeaderboard();
      }
    } catch (error) {
      console.error('Error starting re-ranking:', error);
      toast.error('Failed to start re-ranking');
    }
  };

  const handlePairwiseComplete = () => {
    loadGlobalLeaderboard(); // Refresh the leaderboard
    setEditingBook(null);
    setPairwiseCandidates([]);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            All-Time Leaderboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex gap-3 p-3 rounded-lg bg-muted/50 animate-pulse">
                <div className="w-12 h-16 bg-muted rounded" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (rankedBooks.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            All-Time Leaderboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            No rated books yet. Start rating books to see your leaderboard!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-0 shadow-none sm:border sm:shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            All-Time Leaderboard
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {rankedBooks.map((book, index) => (
              <div
                key={book.book_id}
                className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                <Link 
                  to={`/book/${book.book_id}`}
                  className="flex items-center gap-3 flex-1 min-w-0"
                >
                  {/* Global rank */}
                  <div className="w-10 text-center flex-shrink-0">
                    <span className="text-lg font-bold text-foreground">
                      #{index + 1}
                    </span>
                  </div>

                  {/* Book cover */}
                  {book.books.cover_url && (
                    <img
                      src={book.books.cover_url}
                      alt={book.books.title}
                      className="w-12 h-16 object-cover rounded flex-shrink-0"
                    />
                  )}

                  {/* Book info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold line-clamp-1">
                      {book.books.title}
                    </h4>
                    <p className="text-sm text-muted-foreground line-clamp-1">
                      {book.books.authors.join(', ')}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary" className="text-xs">
                        {book.tier}★
                      </Badge>
                    </div>
                  </div>
                </Link>

                {/* Edit ranking button */}
                {isOwnProfile && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditRanking(book)}
                    className="opacity-60 hover:opacity-100 flex-shrink-0"
                  >
                    <Edit3 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pairwise comparison sheet */}
      {editingBook && (
        <PairwiseSheet
          isOpen={!!editingBook}
          onClose={() => {
            setEditingBook(null);
            setPairwiseCandidates([]);
          }}
          currentBook={{
            id: editingBook.book_id,
            title: editingBook.books.title,
            authors: editingBook.books.authors,
            cover_url: editingBook.books.cover_url
          }}
          tier={editingBook.tier}
          candidates={pairwiseCandidates}
          onComplete={handlePairwiseComplete}
          placementState={editingBook.placementState}
          comparison={editingBook.comparison}
        />
      )}
    </>
  );
}